/**
 * IndexedDB — der lokale Zwischenspeicher der App.
 *
 * Zwei Regeln bestimmen den Aufbau:
 *
 * 1. **Messpunkte liegen pro Tauchgang in einem Datensatz**, nicht als eine
 *    Million einzelner Zeilen. IndexedDB wird bei einer Million Schreibvorgaengen
 *    unbrauchbar langsam; ein Datensatz je Tauchgang laedt in Millisekunden.
 * 2. **Kopfdaten und Profile sind getrennt.** Die Liste braucht 995 Kopfdaten,
 *    das Profil erst, wenn jemand einen Tauchgang oeffnet.
 */

const NAME = 'tauchlogbuch';
const VERSION = 3;

export const SPEICHER = {
  meta: 'meta',
  dives: 'dives',
  samples: 'samples',
  sites: 'sites',
  trips: 'trips',
  buddies: 'buddies',
  gear: 'gear',
  // Merkzettel gelöschter Sätze: Kennung, Speicher, Zeitpunkt. Ohne sie legt
  // ein ergänzender Import einen gelöschten Tauchgang wieder an.
  grabsteine: 'grabsteine',
  // Check-in: Inhalte verschlüsselt, siehe checkin.js. Bewusst getrennt von
  // den Tauchgängen — ein neues Bundle ersetzt die Tauchgänge, die Ausweise
  // dürfen dabei nicht verschwinden.
  checkin: 'checkin',
  tresor: 'tresor',
};

/** Speicher, deren Sätze einen Änderungszeitstempel tragen. */
export const MIT_ZEITSTEMPEL = [SPEICHER.dives, SPEICHER.sites, SPEICHER.trips,
  SPEICHER.buddies, SPEICHER.gear];

export function oeffne(indexedDB = globalThis.indexedDB) {
  return new Promise((erfuellen, ablehnen) => {
    const anfrage = indexedDB.open(NAME, VERSION);
    anfrage.onupgradeneeded = (ereignis) => {
      const db = anfrage.result;
      if (!db.objectStoreNames.contains(SPEICHER.meta)) {
        db.createObjectStore(SPEICHER.meta);
      }
      if (!db.objectStoreNames.contains(SPEICHER.dives)) {
        const dives = db.createObjectStore(SPEICHER.dives, { keyPath: 'id' });
        dives.createIndex('start', 'start_utc');
        dives.createIndex('nummer', 'dive_number');
      }
      if (!db.objectStoreNames.contains(SPEICHER.samples)) {
        db.createObjectStore(SPEICHER.samples, { keyPath: 'dive_id' });
      }
      for (const name of [SPEICHER.sites, SPEICHER.trips, SPEICHER.buddies, SPEICHER.gear,
        SPEICHER.checkin]) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      }
      if (!db.objectStoreNames.contains(SPEICHER.tresor)) {
        db.createObjectStore(SPEICHER.tresor);
      }
      if (!db.objectStoreNames.contains(SPEICHER.grabsteine)) {
        db.createObjectStore(SPEICHER.grabsteine, { keyPath: 'id' });
      }

      // Wer von Version 2 kommt, hat Sätze ohne `geaendert`. Der Abgleich
      // braucht den Wert, also wird er einmalig nachgetragen: aus `updated_at`
      // der Quelle, ersatzweise aus dem Zeitpunkt des Umstiegs.
      if (ereignis.oldVersion > 0 && ereignis.oldVersion < 3) {
        const jetzt = new Date().toISOString();
        for (const name of MIT_ZEITSTEMPEL) {
          if (!db.objectStoreNames.contains(name)) continue;
          const store = anfrage.transaction.objectStore(name);
          store.openCursor().onsuccess = (e) => {
            const zeiger = e.target.result;
            if (!zeiger) return;
            const satz = zeiger.value;
            if (!satz.geaendert) {
              satz.geaendert = satz.updated_at ?? jetzt;
              zeiger.update(satz);
            }
            zeiger.continue();
          };
        }
      }
    };
    anfrage.onsuccess = () => erfuellen(anfrage.result);
    anfrage.onerror = () => ablehnen(anfrage.error);
    // Ein Versionswechsel braucht alle anderen Verbindungen geschlossen. Ohne
    // diesen Zweig wartet die App stumm, und niemand kaeme auf den zweiten Tab.
    anfrage.onblocked = () => ablehnen(new Error(
      'Das Tauchlogbuch ist in einem anderen Tab geöffnet. '
      + 'Schliesse ihn, damit die Datenbank umgestellt werden kann.',
    ));
  });
}

function abschluss(transaktion) {
  return new Promise((erfuellen, ablehnen) => {
    transaktion.oncomplete = () => erfuellen();
    transaktion.onerror = () => ablehnen(transaktion.error);
    transaktion.onabort = () => ablehnen(transaktion.error);
  });
}

function ergebnis(anfrage) {
  return new Promise((erfuellen, ablehnen) => {
    anfrage.onsuccess = () => erfuellen(anfrage.result);
    anfrage.onerror = () => ablehnen(anfrage.error);
  });
}

/**
 * Die Tauchgangsdaten loeschen — ein neues Bundle ersetzt den bisherigen Stand.
 *
 * **Der Check-in bleibt stehen.** Er kommt nicht aus dem Bundle, sondern wird
 * in der App erfasst; ein Datenimport darf die Ausweise nicht mitnehmen.
 */
export async function leeren(db) {
  const namen = Object.values(SPEICHER)
    .filter((name) => name !== SPEICHER.checkin && name !== SPEICHER.tresor);
  const t = db.transaction(namen, 'readwrite');
  for (const name of namen) t.objectStore(name).clear();
  await abschluss(t);
}

/**
 * Einen Speicher leeren und neu befuellen.
 *
 * Fuer die Sicherungskopie: Zurueckgeholt wird ein Stand, kein Zuwachs — was
 * hier lag und dort fehlt, muss verschwinden. Deshalb `clear()` und nicht nur
 * `put()`.
 *
 * Geleert wird in derselben Transaktion wie das erste Stueck; ginge das Leeren
 * fuer sich durch und der Rest schluege fehl, stuende der Speicher leer da.
 * Die weiteren Stuecke folgen wie bei `schreibe()` einzeln, damit die
 * Oberflaeche bei 995 Tauchgaengen ansprechbar bleibt.
 */
export async function ersetze(db, speicher, datensaetze, melde = null) {
  const stueck = 200;
  const erstes = datensaetze.slice(0, stueck);
  const t = db.transaction(speicher, 'readwrite');
  const store = t.objectStore(speicher);
  store.clear();
  for (const satz of erstes) store.put(satz);
  await abschluss(t);
  if (melde) melde(erstes.length, datensaetze.length);
  if (datensaetze.length > stueck) {
    await schreibe(db, speicher, datensaetze.slice(stueck),
      (fertig, gesamt) => melde?.(stueck + fertig, stueck + gesamt));
  }
}

/**
 * Datensaetze stueckweise schreiben.
 *
 * Eine einzige Transaktion ueber 995 Tauchgaenge samt Profilen blockiert den
 * Hauptstrang sichtbar; in Haeppchen bleibt die Oberflaeche ansprechbar und
 * der Fortschritt laesst sich melden.
 */
export async function schreibe(db, speicher, datensaetze, melde = null) {
  const stueck = 200;
  for (let i = 0; i < datensaetze.length; i += stueck) {
    const t = db.transaction(speicher, 'readwrite');
    const store = t.objectStore(speicher);
    for (const satz of datensaetze.slice(i, i + stueck)) store.put(satz);
    await abschluss(t);
    if (melde) melde(Math.min(i + stueck, datensaetze.length), datensaetze.length);
  }
}

export async function setzeMeta(db, schluessel, wert) {
  const t = db.transaction(SPEICHER.meta, 'readwrite');
  t.objectStore(SPEICHER.meta).put(wert, schluessel);
  await abschluss(t);
}

export async function holeMeta(db, schluessel) {
  const t = db.transaction(SPEICHER.meta, 'readonly');
  return ergebnis(t.objectStore(SPEICHER.meta).get(schluessel));
}

/** Alle Kopfdaten, chronologisch — die Grundlage der Liste. */
export async function alleTauchgaenge(db) {
  const t = db.transaction(SPEICHER.dives, 'readonly');
  const liste = await ergebnis(t.objectStore(SPEICHER.dives).getAll());
  return liste.sort((a, b) => String(b.start_utc).localeCompare(String(a.start_utc)));
}

export async function tauchgang(db, id) {
  const t = db.transaction(SPEICHER.dives, 'readonly');
  return ergebnis(t.objectStore(SPEICHER.dives).get(id));
}

/** Messpunkte eines Tauchgangs — erst auf Abruf, nie auf Vorrat. */
export async function messpunkte(db, diveId) {
  const t = db.transaction(SPEICHER.samples, 'readonly');
  const satz = await ergebnis(t.objectStore(SPEICHER.samples).get(diveId));
  return satz ? satz.werte : null;
}

/** Alle Datensaetze eines Speichers — fuer Statistik und Karte. */
export async function alleAus(db, speicher) {
  const t = db.transaction(speicher, 'readonly');
  return ergebnis(t.objectStore(speicher).getAll());
}

export async function stammsatz(db, speicher, id) {
  if (!id) return null;
  const t = db.transaction(speicher, 'readonly');
  return ergebnis(t.objectStore(speicher).get(id));
}

export async function zaehle(db, speicher) {
  const t = db.transaction(speicher, 'readonly');
  return ergebnis(t.objectStore(speicher).count());
}
